import React, { useEffect, useMemo, useState } from 'react';
import { Box, List, ListItemButton, ListItemText, Typography, LinearProgress, Collapse, IconButton } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';

export type InPageSection = {
  id: string;
  label: string;
};

interface InPageNavSidebarProps {
  sections: InPageSection[];
  offsetTop?: number; // extra offset for fixed headers
}

const InPageNavSidebar: React.FC<InPageNavSidebarProps> = ({ sections, offsetTop = 80 }) => {
  const [activeId, setActiveId] = useState<string>(sections[0]?.id || '');
  const [expanded, setExpanded] = useState(true);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [sectionProgress, setSectionProgress] = useState(0);
  const [maxHeight, setMaxHeight] = useState(`calc(100vh - ${offsetTop + 100}px)`);
  const [sidebarScrollTop, setSidebarScrollTop] = useState(0);

  const sectionIds = useMemo(() => sections.map(s => s.id), [sections]);

  // Calculate scroll progress and section progress
  useEffect(() => {
    const updateScrollProgress = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = Math.min((scrollTop / docHeight) * 100, 100);
      setScrollProgress(progress);

      // Calculate progress within current section
      const activeIndex = sections.findIndex(s => s.id === activeId);
      if (activeIndex >= 0) {
        const currentSection = document.getElementById(activeId);
        const nextSection = activeIndex < sections.length - 1 ? 
          document.getElementById(sections[activeIndex + 1].id) : null;
        
        if (currentSection && nextSection) {
          const currentTop = currentSection.offsetTop;
          const nextTop = nextSection.offsetTop;
          const currentScroll = Math.max(0, scrollTop - currentTop);
          const sectionHeight = nextTop - currentTop;
          const sectionProgressValue = Math.min((currentScroll / sectionHeight) * 100, 100);
          setSectionProgress(sectionProgressValue);
        } else if (currentSection) {
          // If it's the last section, calculate progress to end of page
          const currentTop = currentSection.offsetTop;
          const pageHeight = document.documentElement.scrollHeight - window.innerHeight;
          const currentScroll = Math.max(0, scrollTop - currentTop);
          const remainingHeight = pageHeight - currentTop;
          const sectionProgressValue = Math.min((currentScroll / remainingHeight) * 100, 100);
          setSectionProgress(sectionProgressValue);
        }
      }
    };

    const updateSidebarScroll = () => {
      const activeIndex = sections.findIndex(s => s.id === activeId);
      if (activeIndex >= 0 && expanded) {
        // Calculate the position to center the active item
        const itemHeight = 48; // Approximate height of each sidebar item
        const sidebarHeight = 300; // Approximate sidebar height
        const centerPosition = (activeIndex * itemHeight) - (sidebarHeight / 2) + (itemHeight / 2);
        setSidebarScrollTop(Math.max(0, Math.min(centerPosition, (sections.length - 1) * itemHeight - sidebarHeight + itemHeight)));
      }
    };

    const updateMaxHeight = () => {
      // Find the footer element
      const footer = document.querySelector('footer') || document.querySelector('[data-footer]');
      if (footer) {
        const footerRect = footer.getBoundingClientRect();
        const footerTop = footerRect.top + window.scrollY;
        const currentScroll = window.scrollY;
        const viewportHeight = window.innerHeight;
        
        // Calculate available height based on footer position
        const footerDistance = footerTop - currentScroll;
        const availableHeight = Math.min(footerDistance - 80, viewportHeight - offsetTop - 80);
        setMaxHeight(`${Math.max(200, availableHeight)}px`);
      } else {
        // Fallback to viewport height with better calculation
        const viewportHeight = window.innerHeight;
        const availableHeight = viewportHeight - offsetTop - 80;
        setMaxHeight(`${Math.max(200, availableHeight)}px`);
      }
    };

    // Set up observers
    const scrollObserver = () => {
      updateScrollProgress();
      updateSidebarScroll(); // Update sidebar scroll position
      updateMaxHeight(); // Update height to prevent footer overlap
    };

    window.addEventListener('scroll', scrollObserver);
    window.addEventListener('resize', updateMaxHeight);
    
    // Initial calculations
    updateScrollProgress();
    updateSidebarScroll();
    updateMaxHeight();

    // Set up MutationObserver to watch for footer changes
    const mutationObserver = new MutationObserver(() => {
      updateMaxHeight();
    });

    // Start observing
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });

    return () => {
      window.removeEventListener('scroll', scrollObserver);
      window.removeEventListener('resize', updateMaxHeight);
      mutationObserver.disconnect();
    };
  }, [activeId, sections, offsetTop, expanded]);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    sectionIds.forEach(id => {
      const el = document.getElementById(id);
      if (!el) return;
      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              setActiveId(id);
            }
          });
        },
        {
          root: null,
          rootMargin: `-${offsetTop + 10}px 0px -60% 0px`,
          threshold: [0, 0.2, 0.4, 0.6, 0.8, 1],
        }
      );
      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach(o => o.disconnect());
  }, [sectionIds, offsetTop]);

  const handleClick = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - offsetTop;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const getActiveIndex = () => {
    return sections.findIndex(section => section.id === activeId);
  };

  const activeIndex = getActiveIndex();

  return (
    <Box
      sx={{
        position: 'fixed',
        top: `${offsetTop}px`,
        left: { xs: '10px', sm: '15px', md: '20px' },
        width: { xs: '260px', sm: '270px', md: '280px' },
        borderRadius: '12px',
        border: '2px solid rgba(71,118,230,0.1)',
        bgcolor: 'background.paper',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        maxHeight: maxHeight,
        display: { xs: 'none', lg: 'flex' }, // Only show on large screens and up
        flexDirection: 'column',
        zIndex: 1001,
        height: 'auto',
      }}
    >
       {/* Header - Always visible */}
       <Box
         sx={{
           p: 2,
           borderBottom: '1px solid #eee',
           flexShrink: 0,
           bgcolor: 'rgba(71,118,230,0.05)',
           position: 'relative',
           '&::after': {
             content: '""',
             position: 'absolute',
             top: '50%',
             right: 12,
             transform: 'translateY(-50%)',
             width: 8,
             height: 8,
             borderRadius: '50%',
             bgcolor: 'success.main',
             animation: 'pulse 2s infinite',
             '@keyframes pulse': {
               '0%': { boxShadow: '0 0 0 0 rgba(76, 175, 80, 0.7)' },
               '70%': { boxShadow: '0 0 0 4px rgba(76, 175, 80, 0)' },
               '100%': { boxShadow: '0 0 0 0 rgba(76, 175, 80, 0)' },
             }
           }
         }}
       >
         <Typography variant="overline" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
           Navigation
         </Typography>
         <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
           {sections[activeIndex]?.label || 'Available'}
         </Typography>
       </Box>

      {/* Section Progress Flow */}
      <Box sx={{ 
        p: 2,
        borderBottom: '1px solid #eee',
        flexShrink: 0,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Box sx={{ 
            width: 8, 
            height: 8, 
            borderRadius: '50%', 
            bgcolor: 'primary.main',
            boxShadow: '0 0 8px rgba(71,118,230,0.4)'
          }} />
          <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 500 }}>
            Current: {sections[activeIndex]?.label}
          </Typography>
        </Box>
        
        {/* Progress line from current to next */}
        <Box sx={{ 
          position: 'relative', 
          height: 4, 
          bgcolor: 'rgba(0,0,0,0.1)', 
          borderRadius: 2,
          overflow: 'hidden',
          mb: 1
        }}>
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: `${sectionProgress}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #4776E6 0%, #8E54E9 100%)',
              borderRadius: 2,
              transition: 'width 0.3s ease',
            }}
          />
        </Box>
        
        {activeIndex < sections.length - 1 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ 
              width: 6, 
              height: 6, 
              borderRadius: '50%', 
              bgcolor: 'rgba(71,118,230,0.3)',
            }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Next: {sections[activeIndex + 1]?.label}
            </Typography>
          </Box>
        )}
      </Box>

       {/* Navigation items - Collapsible with Clock-like Animation */}
       <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative', minHeight: '200px' }}>
         <Box sx={{ p: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
           <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexShrink: 0 }}>
             <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
               All Sections
             </Typography>
             <IconButton 
               size="small" 
               onClick={() => setExpanded(!expanded)}
               sx={{ p: 0.5 }}
             >
               {expanded ? <ExpandLess /> : <ExpandMore />}
             </IconButton>
           </Box>
           
           <Collapse in={expanded} timeout="auto" unmountOnExit>
             <Box sx={{ 
               flex: 1,
               overflow: 'auto', 
               position: 'relative',
               borderRadius: '8px',
               border: '1px solid rgba(0,0,0,0.1)',
               bgcolor: 'rgba(0,0,0,0.02)',
               minHeight: '200px',
               maxHeight: '400px',
               '&::-webkit-scrollbar': { 
                 width: '6px',
                 '&:hover': { width: '8px' }
               },
               '&::-webkit-scrollbar-track': {
                 background: 'rgba(0,0,0,0.1)',
                 borderRadius: '3px'
               },
               '&::-webkit-scrollbar-thumb': {
                 background: 'rgba(71,118,230,0.3)',
                 borderRadius: '3px',
                 '&:hover': {
                   background: 'rgba(71,118,230,0.5)'
                 }
               }
             }}>
               {/* Scrollable content */}
               <Box sx={{
                 position: 'relative',
                 transform: `translateY(-${sidebarScrollTop}px)`,
                 transition: 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
               }}>
                <List dense sx={{ py: 2 }}>
                  {sections.map((section, index) => {
                    const isActive = activeId === section.id;
                    const isCompleted = index < activeIndex;
                    const isUpcoming = index > activeIndex;
                    
                    return (
                      <ListItemButton
                        key={section.id}
                        onClick={() => handleClick(section.id)}
                        selected={isActive}
                        sx={{
                          borderRadius: '12px',
                          mb: 1,
                          mx: 1,
                          position: 'relative',
                          minHeight: '48px',
                          transform: isActive ? 'scale(1.05)' : 'scale(1)',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&.Mui-selected': {
                            bgcolor: 'rgba(71,118,230,0.15)',
                            boxShadow: '0 4px 12px rgba(71,118,230,0.2)',
                            '&:before': {
                              content: '""',
                              position: 'absolute',
                              left: 0,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              width: 4,
                              height: '70%',
                              bgcolor: 'primary.main',
                              borderRadius: '0 4px 4px 0',
                            }
                          },
                          '&:hover': {
                            bgcolor: isActive ? 'rgba(71,118,230,0.2)' : 'rgba(71,118,230,0.08)',
                            transform: isActive ? 'scale(1.05)' : 'scale(1.02)',
                          }
                        }}
                      >
                        <ListItemText
                          primary={section.label}
                          primaryTypographyProps={{ 
                            fontSize: isActive ? 14 : 13,
                            fontWeight: isActive ? 700 : 500,
                            color: isActive ? 'primary.main' : isCompleted ? 'success.main' : 'text.primary',
                            sx: {
                              transition: 'all 0.3s ease'
                            }
                          }}
                        />
                        
                        {/* Enhanced status indicator with animation */}
                        <Box sx={{ 
                          position: 'relative',
                          ml: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '20px',
                          height: '20px'
                        }}>
                          {isCompleted && (
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: 'success.main',
                                animation: 'pulse 2s infinite',
                                '@keyframes pulse': {
                                  '0%': { boxShadow: '0 0 0 0 rgba(76, 175, 80, 0.7)' },
                                  '70%': { boxShadow: '0 0 0 6px rgba(76, 175, 80, 0)' },
                                  '100%': { boxShadow: '0 0 0 0 rgba(76, 175, 80, 0)' },
                                }
                              }}
                            />
                          )}
                          {isActive && (
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: 'primary.main',
                                boxShadow: '0 0 12px rgba(71,118,230,0.6)',
                                animation: 'glow 1.5s ease-in-out infinite alternate',
                                '@keyframes glow': {
                                  from: { boxShadow: '0 0 8px rgba(71,118,230,0.6)' },
                                  to: { boxShadow: '0 0 16px rgba(71,118,230,0.8)' },
                                }
                              }}
                            />
                          )}
                          {isUpcoming && (
                            <Box
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                bgcolor: 'rgba(0,0,0,0.3)',
                                opacity: 0.6
                              }}
                            />
                          )}
                        </Box>
                      </ListItemButton>
                    );
                  })}
                </List>
              </Box>
            </Box>
          </Collapse>
        </Box>
      </Box>
    </Box>
  );
};

export default InPageNavSidebar;
